import React from 'react';
import { backendUrl, sseBackendUrl, tenantId as staticTenantId } from 'config/envConfig';

const normalizeDeep = (value) => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        return normalizeDeep(JSON.parse(trimmed));
      } catch {
        return value;
      }
    }
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => normalizeDeep(item));
  }

  if (value && typeof value === 'object') {
    return Object.entries(value).reduce((acc, [key, val]) => {
      acc[key] = normalizeDeep(val);
      return acc;
    }, {});
  }

  return value;
};

const closeEventSource = (source) => {
  if (source) {
    try {
      source.close();
    } catch {
      // noop
    }
  }
};

const sanitizeTenant = (tenantId) => {
  if (tenantId === undefined || tenantId === null) {
    return null;
  }

  const text = String(tenantId).trim();
  return text.length > 0 ? text : null;
};

const normalizeCampaignUuid = (value) => {
  if (value === undefined || value === null) {
    return null;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const text = String(value).trim();
    return text.length > 0 ? text : null;
  }

  if (typeof value === 'object') {
    const candidates = [
      value.campaignUuid,
      value.campaign_id,
      value.uuid,
      value.id,
      value.value,
    ].filter((candidate) => candidate !== undefined && candidate !== null);

    if (candidates.length > 0) {
      const candidateText = String(candidates[0]).trim();
      return candidateText.length > 0 ? candidateText : null;
    }
  }

  return null;
};

export const useStreamHubNotifications = () => {
  const [connected, setConnected] = React.useState(false);
  const [failed, setFailed] = React.useState(0);
  const [success, setSuccess] = React.useState(0);
  const [completed, setCompleted] = React.useState(false);
  const [lastError, setLastError] = React.useState(null);
  const [lastRawEvent, setLastRawEvent] = React.useState(null);
  const [lastEvent, setLastEvent] = React.useState(null);

  const esRef = React.useRef(null);
  const countersRef = React.useRef({ success: 0, failed: 0 });
  const currentTopicRef = React.useRef(null);
  const completedRef = React.useRef(false);

  const storageKeyForTopic = React.useCallback((topic) => {
    return topic ? `mpl:lastEventId:${topic}` : null;
  }, []);
  const storageKeyForCounters = React.useCallback((topic) => {
    return topic ? `mpl:counters:${topic}` : null;
  }, []);
  const storageKeyForFromBeginning = React.useCallback((topic) => {
    return topic ? `mpl:fromBeginning:${topic}` : null;
  }, []);

  const loadLastEventId = React.useCallback((topic) => {
    try {
      const key = storageKeyForTopic(topic);
      if (!key) return null;
      const value = sessionStorage.getItem(key);
      return typeof value === 'string' && value.length > 0 ? value : null;
    } catch {
      return null;
    }
  }, [storageKeyForTopic]);

  const saveLastEventId = React.useCallback((topic, id) => {
    try {
      const key = storageKeyForTopic(topic);
      if (!key || !id) return;
      sessionStorage.setItem(key, id);
    } catch {
      // ignore
    }
  }, [storageKeyForTopic]);

  // eslint-disable-next-line no-unused-vars
  const clearLastEventId = React.useCallback((topic) => {
    try {
      const key = storageKeyForTopic(topic);
      if (!key) return;
      sessionStorage.removeItem(key);
    } catch {
      // ignore
    }
  }, [storageKeyForTopic]);

  const markRequestFromBeginning = React.useCallback((topic) => {
    try {
      const key = storageKeyForFromBeginning(topic);
      if (!key) return;
      sessionStorage.setItem(key, '1');
    } catch {
      // ignore
    }
  }, [storageKeyForFromBeginning]);

  const shouldRequestFromBeginning = React.useCallback((topic) => {
    try {
      const key = storageKeyForFromBeginning(topic);
      if (!key) return false;
      return sessionStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  }, [storageKeyForFromBeginning]);

  const clearRequestFromBeginning = React.useCallback((topic) => {
    try {
      const key = storageKeyForFromBeginning(topic);
      if (!key) return;
      sessionStorage.removeItem(key);
    } catch {
      // ignore
    }
  }, [storageKeyForFromBeginning]);

  const loadCounters = React.useCallback((topic) => {
    try {
      const key = storageKeyForCounters(topic);
      if (!key) return null;
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const s = Number(parsed?.success || 0);
      const f = Number(parsed?.failed || 0);
      if (Number.isFinite(s) && Number.isFinite(f)) return { success: s, failed: f };
      return null;
    } catch {
      return null;
    }
  }, [storageKeyForCounters]);

  const saveCounters = React.useCallback((topic, counters) => {
    try {
      const key = storageKeyForCounters(topic);
      if (!key) return;
      const payload = {
        success: Number(counters?.success || 0),
        failed: Number(counters?.failed || 0),
        ts: Date.now(),
      };
      sessionStorage.setItem(key, JSON.stringify(payload));
    } catch {
      // ignore
    }
  }, [storageKeyForCounters]);

  // eslint-disable-next-line no-unused-vars
  const clearCounters = React.useCallback((topic) => {
    try {
      const key = storageKeyForCounters(topic);
      if (!key) return;
      sessionStorage.removeItem(key);
    } catch {
      // ignore
    }
  }, [storageKeyForCounters]);

  const resetState = React.useCallback(() => {
    setConnected(false);
    setFailed(0);
    setSuccess(0);
    setCompleted(false);
    completedRef.current = false;
    setLastError(null);
    setLastRawEvent(null);
    setLastEvent(null);
    countersRef.current = { success: 0, failed: 0 };
  }, []);

  const connect = React.useCallback(
    ({
      campaignUuid,
      tenantId,
      token,
      lastEventId,
      baseUrl = sseBackendUrl || backendUrl,
    }) => {
      const normalizedTenant =
        sanitizeTenant(tenantId) || sanitizeTenant(staticTenantId);
      const normalizedCampaign = normalizeCampaignUuid(campaignUuid);
      const trimmedToken = typeof token === 'string' ? token.trim() : null;
      const effectiveUrl = baseUrl || sseBackendUrl || backendUrl;

      if (!normalizedTenant || !normalizedCampaign || !trimmedToken || !effectiveUrl) {
        console.warn('[SSE] connect -> missing parameters', {
          campaignUuid,
          tenantId,
          hasToken: !!trimmedToken,
          baseUrl: effectiveUrl,
          staticTenantId,
        });
        return;
      }

      if (esRef.current) {
        closeEventSource(esRef.current);
        esRef.current = null;
      }

      resetState();

      const topic = `urn:${normalizedTenant}:campaign_${normalizedCampaign}`;
      currentTopicRef.current = topic;

      // Si no viene explícito, intentar recuperar el último id persistido para pedir backlog
      let effectiveLastEventId = lastEventId;
      if (!effectiveLastEventId) {
        effectiveLastEventId = loadLastEventId(topic);
      }
      // Si marcamos que se solicite desde el inicio de la ventana, forzamos lastEventID=0
      if (shouldRequestFromBeginning(topic)) {
        effectiveLastEventId = '0';
        clearRequestFromBeginning(topic);
      }
      // Si pedimos replay desde el inicio, recomputamos contadores desde cero para evitar duplicados
      if (effectiveLastEventId === '0') {
        countersRef.current = { success: 0, failed: 0 };
        setSuccess(0);
        setFailed(0);
      } else {
        // Rehidratar contadores previos si existen
        const persistedCounters = loadCounters(topic);
        if (persistedCounters) {
          countersRef.current.success = persistedCounters.success;
          countersRef.current.failed = persistedCounters.failed;
          setSuccess(countersRef.current.success);
          setFailed(countersRef.current.failed);
        }
      }

      const params = new URLSearchParams({
        topic,
        token: trimmedToken,
        clientId: normalizedTenant,
      });

      if (effectiveLastEventId) {
        params.set('lastEventID', String(effectiveLastEventId));
      }

      const url = `${effectiveUrl.replace(/\/$/, '')}/api/v1/email-ecommerce-manager/campaigns/streamhub?${params.toString()}`;
      console.log('[SSE] connect -> connecting', {
        urlWithoutToken: url.replace(/token=[^&]+/, 'token=***'),
        normalizedTenant,
        staticTenantId,
        lastEventId: effectiveLastEventId || null,
      });

      const es = new EventSource(url, { withCredentials: true });
      esRef.current = es;
      let streamErrorCount = 0;

      const handleEvent = (evt) => {
        const rawPayload = evt?.data ?? '';
        const payload = typeof rawPayload === 'string' && rawPayload.trim().length > 0
          ? (() => {
              try {
                const parsed = JSON.parse(rawPayload);
                return normalizeDeep(parsed);
              } catch {
                return null;
              }
            })()
          : null;

        setLastRawEvent(rawPayload);

        if (payload) {
          setLastEvent(payload);

          // Persistir lastEventId si viene en el evento
          const lastId = evt && typeof evt.lastEventId === 'string' && evt.lastEventId.length > 0
            ? evt.lastEventId
            : null;
          if (lastId && currentTopicRef.current) {
            saveLastEventId(currentTopicRef.current, lastId);
          }

          if (payload?.type === 'link_processed') {
            // Si ya se recibió un evento final, ignorar incrementos posteriores del backlog
            if (completedRef.current) {
              return;
            }
            const status = String(payload?.status || '').toLowerCase();
            if (status === 'success') {
              countersRef.current.success += 1;
              setSuccess(countersRef.current.success);
            } else if (status === 'failed') {
              countersRef.current.failed += 1;
              setFailed(countersRef.current.failed);
            }
            // Persistir counters en cada avance
            if (currentTopicRef.current) {
              saveCounters(currentTopicRef.current, countersRef.current);
            }
          }

          if (payload?.type === 'campaign_failed') {
            setLastError(payload?.error || payload?.errorCode || 'campaign_failed');
            // Aplicar summary si viene en el evento
            const stats = payload?.stats;
            if (stats && (typeof stats.success === 'number' || typeof stats.failed === 'number')) {
              countersRef.current.success = Number(stats.success || 0);
              countersRef.current.failed = Number(stats.failed || 0);
              setSuccess(countersRef.current.success);
              setFailed(countersRef.current.failed);
            }
            // Persistir counters finales
            if (currentTopicRef.current) {
              saveCounters(currentTopicRef.current, countersRef.current);
            }
            setCompleted(true);
              completedRef.current = true;
          }

          if (payload?.type === 'campaign_completed') {
            // Aplicar summary si viene en el evento
            const stats = payload?.stats;
            if (stats && (typeof stats.success === 'number' || typeof stats.failed === 'number')) {
              countersRef.current.success = Number(stats.success || 0);
              countersRef.current.failed = Number(stats.failed || 0);
              setSuccess(countersRef.current.success);
              setFailed(countersRef.current.failed);
            }
            setCompleted(true);
              completedRef.current = true;
            // Marcar que, si el usuario refresca, se solicite replay desde el inicio de la ventana
            if (currentTopicRef.current) {
              markRequestFromBeginning(currentTopicRef.current);
              // Persistir counters finales (y opcionalmente mantenerlos para relogueo)
              saveCounters(currentTopicRef.current, countersRef.current);
            }
            console.log('[SSE] campaign_completed received', {
              payload,
              readyState: es.readyState,
            });
          }
        }

        console.log('[SSE] message', {
          eventType: evt?.type,
          data: rawPayload,
          parsed: payload,
          readyState: es.readyState,
        });
      };

      es.onopen = () => {
        streamErrorCount = 0;
        setConnected(true);
        setLastError(null);
        console.log('[SSE] open');
      };

      es.onerror = (error) => {
        streamErrorCount += 1;
        setConnected(false);
        setLastError('stream_error');
        console.warn('[SSE] error event', {
          error,
          readyState: es.readyState,
          streamErrorCount,
        });
        // Cap reconnects: EventSource retries forever on abrupt 200+end / StreamHub down
        if (streamErrorCount >= 3 || es.readyState === EventSource.CLOSED) {
          closeEventSource(es);
          if (esRef.current === es) {
            esRef.current = null;
          }
        }
      };

      es.addEventListener('message', handleEvent);
      es.addEventListener('link_processed', handleEvent);
      es.addEventListener('campaign_completed', handleEvent);
      es.addEventListener('campaign_failed', handleEvent);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resetState],
  );

  const disconnect = React.useCallback(
    (preserve = false) => {
      if (esRef.current) {
        closeEventSource(esRef.current);
        esRef.current = null;
      }
      if (preserve) {
        // Mantener contadores y estado de completion para mostrar el resumen
        setConnected(false);
        console.log('[SSE] disconnect invoked (preserve counters)');
        return;
      }
      resetState();
      console.log('[SSE] disconnect invoked');
    },
    [resetState],
  );

  React.useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    connected,
    failed,
    success,
    completed,
    lastError,
    lastRawEvent,
    lastEvent,
    connect,
    disconnect,
  };
};
